import { FormEvent, useRef, useState } from 'react';
import {
  useCryptoDepositRequestMutation,
  useDepositWithdrawRequestMutation,
  useUploadFileMutation,
} from '@/store/api/accountApi';
import { copyToClipboard, getApiErrorMessage, showToast } from '@/utils';
import { Icons } from '@/utils/Icons';
import { extractUtrFromImage } from '@/utils/extractUtr';
import { depositSchema } from '@/utils/validation';
import { TranslationKey, useTranslation } from '@/i18n';
import type { DepositOption } from './depositOptions';
import { DepositOptionIcon } from './DepositMethodList';

type Props = {
  option: DepositOption;
  amount: number;
  onSubmitted: () => void;
};

/** UTR / RRN numbers are 6 to 23 letters or digits. */
const UTR_PATTERN = /^[A-Za-z0-9]{6,23}$/;

type ScanStatus = 'scanning' | 'found' | 'not-found' | null;

const SCAN_MESSAGES: Record<
  Exclude<ScanStatus, null>,
  { text: TranslationKey; className: string }
> = {
  scanning: { text: 'deposit.scanning', className: 'text-shell-muted' },
  found: { text: 'deposit.scanFound', className: 'text-success-emerald' },
  'not-found': { text: 'deposit.scanNotFound', className: 'text-shell-muted' },
};

const sanitizeReference = (value: string, isCrypto: boolean) =>
  isCrypto ? value.replace(/\s/g, '') : value.replace(/[^A-Za-z0-9]/g, '').slice(0, 23);

const copyValue = async (label: string, value: string) => {
  const copied = await copyToClipboard(value);
  showToast(copied ? `${label} copied` : `Could not copy ${label}`, copied ? 'success' : 'error');
};

const QrImage = ({ src, alt }: { src?: string | null; alt: string }) =>
  src ? (
    <div className="mx-auto w-fit rounded-xl bg-surface p-3">
      <img src={src} alt={alt} className="size-56 max-w-full object-contain" />
    </div>
  ) : null;

/** Icon and title above the UPI, bank and crypto details. */
const DetailsHeader = ({ option, title }: { option: DepositOption; title: string }) => (
  <div className="flex items-center gap-3 border-b border-shell-border pb-3 text-base text-shell-fg">
    <DepositOptionIcon kind={option.kind} />
    {title}
  </div>
);

/** "Label ...... value  [Copy]" rows in one box. */
const CopyRows = ({ rows }: { rows: { label: string; value?: string | null }[] }) => {
  const { t } = useTranslation();
  return (
    <div className="space-y-2 rounded-xl bg-shell-raised px-3 py-3">
      {rows
        .filter((row) => row.value)
        .map(({ label, value }) => (
          <div key={label} className="flex items-center justify-between gap-3 text-sm">
            <span className="shrink-0 text-shell-muted">{label}</span>
            <span className="flex min-w-0 items-center gap-2">
              <span className="break-all text-right text-shell-fg">{value}</span>
              <button
                type="button"
                onClick={() => copyValue(label, value!)}
                aria-label={`Copy ${label}`}
                className="flex shrink-0 items-center gap-1 font-semibold text-shell-fg hover:text-gold"
              >
                <Icons.copy className="size-4" />
                {t('deposit.copy')}
              </button>
            </span>
          </div>
        ))}
    </div>
  );
};

/** The account to pay into, laid out per kind. */
const PaymentDetails = ({ option, usdtAmount }: { option: DepositOption; usdtAmount?: number }) => {
  const { t } = useTranslation();

  if (option.kind === 'upi') {
    return (
      <div className="space-y-3">
        <DetailsHeader option={option} title="UPI" />
        {/* Scan the QR, or pay to the UPI ID below. */}
        <QrImage src={option.account.qrCode} alt={`QR code for ${option.account.upiId}`} />
        <CopyRows
          rows={[
            { label: t('deposit.displayName'), value: option.account.name },
            { label: t('deposit.upiDetails'), value: option.account.upiId },
          ]}
        />
      </div>
    );
  }

  if (option.kind === 'bank') {
    const { account } = option;
    return (
      <div className="space-y-3">
        <DetailsHeader option={option} title={t('deposit.bankDetails')} />
        <CopyRows
          rows={[
            { label: t('deposit.bankName'), value: account.bankName },
            { label: t('deposit.accountHolderName'), value: account.accountHolder },
            { label: t('deposit.accountNumber'), value: account.accountNumber },
            { label: t('deposit.ifsc'), value: account.ifsc },
          ]}
        />
      </div>
    );
  }

  const { account, rate } = option;
  return (
    <div className="space-y-3">
      <DetailsHeader option={option} title={t('deposit.cryptoDetails')} />
      {usdtAmount && rate ? (
        <CopyRows
          rows={[
            { label: t('deposit.walletAddress'), value: account.walletAddress },
            { label: t('deposit.amount'), value: `USDT ${usdtAmount}` },
            { label: t('deposit.displayName'), value: option.label },
          ]}
        />
      ) : (
        <p className="rounded-lg border border-danger-red px-3 py-2 text-sm text-danger-red">
          {t('deposit.rateUnavailable')}
        </p>
      )}
      {rate && (
        <p className="text-center text-xs text-shell-muted">
          1 USDT = ₹{rate.toLocaleString('en-IN')}
        </p>
      )}
      <QrImage src={account.qrImage ?? account.qr} alt="Crypto wallet QR code" />
    </div>
  );
};

/**
 * Step 3: the account to pay into, then the payment screenshot and the transaction reference.
 * For UPI and bank screenshots the UTR is read from the image (tesseract.js) and filled in.
 * Submits with the same calls as the /deposit page.
 */
const DepositPaymentStep = ({ option, amount, onSubmitted }: Props) => {
  const { t } = useTranslation();
  const isCrypto = option.kind === 'crypto';

  const [proof, setProof] = useState<File | undefined>();
  const [reference, setReference] = useState('');
  const [scanStatus, setScanStatus] = useState<ScanStatus>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  // Bumped on every new or removed screenshot, so a slow OCR result for an old one is dropped.
  const scanIdRef = useRef(0);
  const autoFilledUtrRef = useRef<string | null>(null);

  const [uploadFile, { isLoading: isUploading }] = useUploadFileMutation();
  const [submitDeposit, { isLoading: isSubmittingFiat }] = useDepositWithdrawRequestMutation();
  const [cryptoDeposit, { isLoading: isSubmittingCrypto }] = useCryptoDepositRequestMutation();
  const isBusy = isUploading || isSubmittingFiat || isSubmittingCrypto;

  // Crypto requests are sent in USDT, converted from the coin amount at the wallet's rate.
  const usdtAmount =
    option.kind === 'crypto' && option.rate ? Number((amount / option.rate).toFixed(2)) : undefined;
  const isReferenceValid = isCrypto ? reference.length >= 6 : UTR_PATTERN.test(reference);
  // A screenshot is optional for crypto (the transaction hash proves it), required otherwise.
  const canSubmit =
    isReferenceValid && (isCrypto ? Boolean(usdtAmount) : Boolean(proof)) && !isBusy;

  const scanForUtr = async (file: File) => {
    const scanId = ++scanIdRef.current;
    setScanStatus('scanning');
    let utr: string | null = null;
    try {
      utr = await extractUtrFromImage(file);
    } catch {
      // OCR failing (e.g. offline) just means the user types the UTR themselves.
    }
    if (scanId !== scanIdRef.current) return;

    const sanitized = utr ? sanitizeReference(utr, false) : '';
    if (!UTR_PATTERN.test(sanitized)) {
      setScanStatus('not-found');
      return;
    }
    autoFilledUtrRef.current = sanitized;
    // Never overwrite a UTR the user already typed or pasted.
    setReference((current) => current || sanitized);
    setScanStatus('found');
  };

  const handleFile = (file?: File) => {
    if (!file) return;
    const result = depositSchema.shape.proof.safeParse(file);
    if (!result.success) {
      showToast(result.error.issues[0]?.message ?? 'Invalid file', 'error');
      return;
    }
    setProof(file);
    // Crypto proofs show a long hash OCR can't read reliably; PDFs can't be OCR'd at all.
    if (!isCrypto && file.type.startsWith('image/')) void scanForUtr(file);
    else setScanStatus(null);
  };

  const removeProof = () => {
    scanIdRef.current++;
    setProof(undefined);
    setScanStatus(null);
    // Take back a UTR the screenshot filled in (the updater runs later, so read the ref now).
    const autoFilledUtr = autoFilledUtrRef.current;
    setReference((current) => (current === autoFilledUtr ? '' : current));
    autoFilledUtrRef.current = null;
    // Let the same file be picked again.
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handlePaste = async () => {
    try {
      setReference(sanitizeReference(await navigator.clipboard.readText(), isCrypto));
    } catch {
      showToast('Could not read the clipboard', 'error');
    }
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!canSubmit) return;

    try {
      let image: string | undefined;
      if (proof) {
        const uploadResponse = await uploadFile(proof).unwrap();
        image = uploadResponse.data?.url ?? uploadResponse.url;
        if (!image) throw new Error('Payment proof upload did not return a file URL');
      }

      const response =
        option.kind === 'crypto'
          ? await cryptoDeposit({
              type: 'Credit',
              amount: usdtAmount!,
              cryptoId: option.account.id,
              transactionCode: reference,
              image,
            }).unwrap()
          : await submitDeposit({
              type: 'Credit',
              amount,
              UTR: reference,
              image,
              method: option.kind === 'bank' ? 'bank' : 'upi',
              ...(option.kind === 'bank'
                ? { bankId: Number(option.account.id) }
                : { UPI: Number(option.account.id) }),
            }).unwrap();

      showToast(response.message || 'Deposit request submitted successfully');
      onSubmitted();
    } catch (error: unknown) {
      showToast(getApiErrorMessage(error, 'Failed to submit deposit request'), 'error');
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <PaymentDetails option={option} usdtAmount={usdtAmount} />

      <div className="flex items-center rounded-xl border border-dashed border-shell-border transition hover:border-gold">
        <input
          ref={fileInputRef}
          type="file"
          id="deposit-proof-upload"
          className="hidden"
          accept="image/jpeg,image/png,image/webp,application/pdf"
          onChange={(event) => handleFile(event.target.files?.[0])}
        />
        <label
          htmlFor="deposit-proof-upload"
          className="flex min-h-[56px] min-w-0 flex-1 cursor-pointer items-center gap-4 px-4 py-3 text-sm"
        >
          <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-gold text-gold-foreground">
            <Icons.plus className="size-5" />
          </span>
          <span className="min-w-0 [overflow-wrap:anywhere]">
            {proof ? proof.name : t('deposit.uploadScreenshot')}
            {isCrypto && !proof && (
              <span className="text-shell-muted"> {t('deposit.optional')}</span>
            )}
          </span>
        </label>
        {proof && (
          <button
            type="button"
            onClick={removeProof}
            aria-label="Remove screenshot"
            className="mr-3 flex size-8 shrink-0 items-center justify-center rounded-full text-shell-muted transition hover:bg-shell-raised hover:text-danger-red"
          >
            <Icons.Cross className="size-5" />
          </button>
        )}
      </div>

      <div>
        <div className="mb-2 flex items-center justify-between gap-2 text-sm">
          <label htmlFor="deposit-reference">
            {isCrypto ? t('deposit.hashCode') : t('deposit.utrLabel')}
          </label>
        </div>
        <div className="flex h-12 items-center rounded-xl border border-shell-border bg-shell-raised focus-within:border-gold">
          <input
            id="deposit-reference"
            value={reference}
            onChange={(event) => setReference(sanitizeReference(event.target.value, isCrypto))}
            autoComplete="off"
            placeholder={isCrypto ? t('deposit.enterHash') : t('deposit.utrPlaceholder')}
            className="h-full min-w-0 flex-1 bg-transparent px-4 text-sm text-shell-fg outline-none placeholder:text-shell-muted"
          />
          <button
            type="button"
            onClick={handlePaste}
            className="flex h-full shrink-0 items-center gap-1 px-4 text-sm font-semibold text-shell-fg hover:text-gold"
          >
            <Icons.paste />
            {t('deposit.paste')}
          </button>
        </div>
        {scanStatus && (
          <p
            role="status"
            className={`mt-2 flex items-center gap-2 text-xs ${SCAN_MESSAGES[scanStatus].className}`}
          >
            {scanStatus === 'scanning' && (
              <span className="size-3 animate-spin rounded-full border-2 border-gold border-t-transparent" />
            )}
            {t(SCAN_MESSAGES[scanStatus].text)}
          </p>
        )}
      </div>

      <button
        type="submit"
        disabled={!canSubmit}
        className="flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-gold text-base font-semibold text-gold-foreground transition hover:bg-gold-hover disabled:cursor-not-allowed disabled:opacity-50"
      >
        {isBusy ? t('deposit.submitting') : t('deposit.submit')}
      </button>
    </form>
  );
};

export default DepositPaymentStep;
